import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, IconButton, Typography, Tooltip, Link } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import { collection, doc, onSnapshot, orderBy, query, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../../../Config/firebase';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import DragDropWrapper from './DragDropWrapper';

type BoardColumn = {
  id: string;
  title: string;
  index: number;
};

type BoardItem = {
  id: string;
  title: string;
  description?: string;
  columnId: string;
  order?: number;
  createdAt?: number;
  updatedAt?: number;
  orderData?: {
    studentName: string;
    subscriptionName: string;
    amountDue: number;
    mobileNumber: string;
    orderId: string;
    paymentLink?: string;
  };
};

type GenericBoardProps = {
  userId: string;
  boardId: string;
  onRequestAddItem?: (columnId: string) => void;
};

// Color themes for different boards
const BOARD_COLORS = [
  { primary: '#1976d2', light: '#e3f2fd', dark: '#1565c0' }, // Blue
  { primary: '#388e3c', light: '#e8f5e8', dark: '#2e7d32' }, // Green
  { primary: '#f57c00', light: '#fff3e0', dark: '#ef6c00' }, // Orange
  { primary: '#7b1fa2', light: '#f3e5f5', dark: '#6a1b9a' }, // Purple
  { primary: '#d32f2f', light: '#ffebee', dark: '#c62828' }, // Red
  { primary: '#00838f', light: '#e0f2f1', dark: '#00695c' }, // Teal
  { primary: '#ff8f00', light: '#fff8e1', dark: '#f57c00' }, // Amber
  { primary: '#6d4c41', light: '#efebe9', dark: '#5d4037' }, // Brown
];

const getBoardColor = (boardId: string) => {
  // Generate a consistent color based on board ID
  const hash = boardId.split('').reduce((a, b) => {
    a = ((a << 5) - a + b.charCodeAt(0)) & 0xffffffff;
    return a;
  }, 0);
  return BOARD_COLORS[Math.abs(hash) % BOARD_COLORS.length];
};

const columnContainerStyle: React.CSSProperties = {
  display: 'flex',
  gap: 16,
  overflowX: 'auto',
  overflowY: 'hidden',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'thin',
  paddingBottom: '8px',
};

const getColumnStyle = (boardId: string): React.CSSProperties => {
  const colors = getBoardColor(boardId);
  return {
    minWidth: 260,
    width: 300,
    flexShrink: 0,
    background: colors.light,
    border: `1px solid ${colors.primary}30`,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 200px)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  };
};

const getColumnHeaderStyle = (boardId: string): React.CSSProperties => {
  const colors = getBoardColor(boardId);
  return {
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.primary}20`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: colors.primary,
    color: 'white',
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    fontSize: '14px',
    fontWeight: 500,
  };
};

const columnContentStyle: React.CSSProperties = {
  padding: 8,
  overflowY: 'auto',
  flex: 1,
  minHeight: 100,
};

export default function GenericBoard({ userId, boardId, onRequestAddItem }: GenericBoardProps) {
  const [columns, setColumns] = useState<BoardColumn[]>([]);
  const [items, setItems] = useState<BoardItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!userId || !boardId) return;

    const columnsQuery = query(
      collection(db, `ServiceProviders/${userId}/InflowBoards/${boardId}/Columns`),
      orderBy('index', 'asc')
    );
    const unsubscribeColumns = onSnapshot(columnsQuery, (snap) => {
      const cols: BoardColumn[] = [];
      snap.forEach((d) => cols.push({ id: d.id, ...(d.data() as Omit<BoardColumn, 'id'>) }));
      setColumns(cols);
    });

    const itemsQuery = query(
      collection(db, `ServiceProviders/${userId}/InflowBoards/${boardId}/Items`),
      orderBy('order', 'asc')
    );
    const unsubscribeItems = onSnapshot(itemsQuery, (snap) => {
      // Only update items if we're not currently dragging to prevent flicker
      if (!isDragging) {
        const itms: BoardItem[] = [];
        snap.forEach((d) => itms.push({ id: d.id, ...(d.data() as Omit<BoardItem, 'id'>) }));
        setItems(itms);
      }
    });

    return () => {
      unsubscribeColumns();
      unsubscribeItems();
    };
  }, [userId, boardId, isDragging]);

  const itemsByColumn = useMemo(() => {
    const map: Record<string, BoardItem[]> = {};
    for (const col of columns) map[col.id] = [];
    for (const item of items) {
      if (!map[item.columnId]) map[item.columnId] = [];
      map[item.columnId].push(item);
    }
    // Ensure stable sort by order then createdAt
    Object.values(map).forEach((list) => list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.createdAt ?? 0) - (a.createdAt ?? 0)));
    return map;
  }, [columns, items]);

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = async (result: any) => {
    const { destination, draggableId, source } = result;
    if (!destination) {
      setIsDragging(false);
      return;
    }

    const newColumnId = destination.droppableId;
    const sourceColumnId = source.droppableId;
    const newOrder = destination.index;

    // Optimistic update - update local state immediately
    setItems(prevItems => {
      const updatedItems = [...prevItems];
      const itemIndex = updatedItems.findIndex(item => item.id === draggableId);
      
      if (itemIndex !== -1) {
        const item = updatedItems[itemIndex];
        updatedItems[itemIndex] = {
          ...item,
          columnId: newColumnId,
          order: newOrder,
          updatedAt: Date.now()
        };
        
        // Reorder items in the destination column
        const destItems = updatedItems.filter(item => item.columnId === newColumnId);
        destItems.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        
        // Update order for all items in the destination column
        destItems.forEach((item, index) => {
          const globalIndex = updatedItems.findIndex(globalItem => globalItem.id === item.id);
          if (globalIndex !== -1) {
            updatedItems[globalIndex] = { ...item, order: index };
          }
        });
      }
      
      return updatedItems;
    });

    // Update server
    const itemRef = doc(db, `ServiceProviders/${userId}/InflowBoards/${boardId}/Items/${draggableId}`);
    try {
      await updateDoc(itemRef, {
        columnId: newColumnId,
        order: newOrder,
        updatedAt: Date.now(),
      });
    } catch (e) {
      console.error('Error updating item position:', e);
      // Revert optimistic update on error
      // The onSnapshot will sync the correct state
    }

    // Small delay before allowing onSnapshot updates to prevent flicker
    setTimeout(() => {
      setIsDragging(false);
    }, 100);
  };

  const handleDeleteCard = async (itemId: string) => {
    try {
      const itemRef = doc(db, `ServiceProviders/${userId}/InflowBoards/${boardId}/Items`, itemId);
      await deleteDoc(itemRef);
    } catch (error) {
      console.error('Error deleting card:', error);
    }
  };

  const handleDeleteColumn = async (columnId: string, columnTitle: string) => {
    try {
      // Check if column has any items
      const itemsInColumn = itemsByColumn[columnId] || [];
      if (itemsInColumn.length > 0) {
        alert(`Cannot delete column "${columnTitle}": It contains ${itemsInColumn.length} card(s). Please move or delete all cards first.`);
        return;
      }
      
      // Delete the column
      const columnRef = doc(db, `ServiceProviders/${userId}/InflowBoards/${boardId}/Columns`, columnId);
      await deleteDoc(columnRef);
    } catch (error) {
      console.error('Error deleting column:', error);
    }
  };

  const handleShareWhatsApp = (item: BoardItem) => {
    if (!item.orderData) return;
    
    const message = `Hi ${item.orderData.studentName},\n\nThis is a reminder for your subscription: ${item.orderData.subscriptionName}\nAmount Due: ₹${item.orderData.amountDue}\n\nPlease complete your payment using the link below:\n${item.orderData.paymentLink || 'Payment link not available'}\n\nThank you!`;
    
    const whatsappUrl = `https://wa.me/${item.orderData.mobileNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const boardColors = getBoardColor(boardId);

  return (
    <Box sx={{ mt: 2 }}>
      {isDragging && (
        <Box sx={{ 
          mb: 2, 
          p: 1.5, 
          bgcolor: 'info.50', 
          borderRadius: 2, 
          border: '1px solid #e3f2fd',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Typography variant="body2" color="info.main" sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Moving card... Drop to complete
          </Typography>
        </Box>
      )}
      <DragDropWrapper onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Box 
          style={columnContainerStyle}
          sx={{
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
            },
          }}
        >
          {columns.map((column) => (
            <Box key={column.id} style={getColumnStyle(boardId)}>
              <Box style={getColumnHeaderStyle(boardId)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {column.title}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    ({itemsByColumn[column.id]?.length || 0})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {onRequestAddItem && (
                    <Tooltip title="Add card">
                      <IconButton 
                        size="small" 
                        onClick={() => onRequestAddItem(column.id)}
                        sx={{ 
                          color: 'white', 
                          p: 0.5,
                          '&:hover': { background: 'rgba(255,255,255,0.2)' } 
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* Show delete button only if column is empty AND not "To Initiate" */}
                  {(!itemsByColumn[column.id] || itemsByColumn[column.id].length === 0) && column.title !== 'To Initiate' && (
                    <Tooltip title="Delete empty column">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteColumn(column.id, column.title)}
                        sx={{ 
                          color: 'white', 
                          p: 0.5,
                          '&:hover': { background: 'rgba(255,255,255,0.2)' } 
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              <Droppable droppableId={column.id}>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} style={columnContentStyle}>
                    {(itemsByColumn[column.id] || []).map((item, index) => (
                      <Draggable draggableId={item.id} index={index} key={item.id}>
                        {(draggableProvided) => (
                          <div ref={draggableProvided.innerRef} {...draggableProvided.draggableProps} {...draggableProvided.dragHandleProps} style={{ marginBottom: 12, ...draggableProvided.draggableProps.style }}>
                            <Card 
                              variant="outlined" 
                              sx={{ 
                                border: `1px solid ${boardColors.primary}20`,
                                borderRadius: 2,
                                '&:hover': { 
                                  border: `1px solid ${boardColors.primary}60`,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }
                              }}
                            >
                              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Typography variant="body2" fontWeight={500} color={boardColors.dark} sx={{ lineHeight: 1.3 }}>
                                    {item.title}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                                    {item.orderData?.paymentLink && (
                                      <Tooltip title="Share payment reminder on WhatsApp">
                                        <IconButton 
                                          size="small" 
                                          onClick={() => handleShareWhatsApp(item)}
                                          sx={{ p: 0.5, color: 'success.main' }}
                                        >
                                          <ShareIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Delete card">
                                      <IconButton 
                                        size="small" 
                                        onClick={() => handleDeleteCard(item.id)}
                                        sx={{ p: 0.5, color: 'error.main' }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                                
                                {/* Show order details if available - more compact */}
                                {item.orderData && (
                                  <Box sx={{ 
                                    mt: 1, 
                                    p: 1, 
                                    bgcolor: 'grey.50', 
                                    borderRadius: 1, 
                                    fontSize: '11px',
                                    border: '1px solid #f0f0f0'
                                  }}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                                      <Typography variant="caption" sx={{ color: '#666' }}>
                                        {item.orderData.studentName}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: '#666' }}>
                                        ₹{item.orderData.amountDue}
                                      </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                      {item.orderData.subscriptionName}
                                    </Typography>
                                    {/* Phone number - clickable for calling */}
                                    <Typography variant="caption" sx={{ 
                                      mt: 0.5, 
                                      display: 'block',
                                      color: '#2563eb',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      '&:hover': { color: '#1d4ed8' }
                                    }}
                                    onClick={() => {
                                      if (item.orderData?.mobileNumber) {
                                        window.open(`tel:${item.orderData.mobileNumber}`, '_self');
                                      }
                                    }}
                                    >
                                      📞 {item.orderData.mobileNumber}
                                    </Typography>
                                    {item.orderData.paymentLink && (
                                      <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                                        <Link 
                                          href={item.orderData.paymentLink} 
                                          target="_blank" 
                                          rel="noopener noreferrer" 
                                          sx={{ 
                                            color: 'primary.main', 
                                            textDecoration: 'underline',
                                            fontSize: '11px'
                                          }}
                                        >
                                          Payment Link
                                        </Link>
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </Box>
          ))}
        </Box>
      </DragDropWrapper>
    </Box>
  );
}


